import type { ComputedRef, InjectionKey } from 'vue'

/**
 * Context a FormField publishes to whatever control sits in its slot.
 *
 * This is what stops every call site from repeating itself: FormField already
 * knows the label, the error and the generated id, so a BaseInput inside one
 * picks up its invalid state, its `id` (matching the label's `for`) and its
 * `aria-describedby` without being told twice.
 *
 * A control used outside a FormField injects nothing and falls back to its own
 * props, so the primitives stay usable standalone.
 */
export interface FieldContext {
  /** id of the control, matching the label's `for` attribute */
  id: ComputedRef<string>
  /** true when the field is showing an error */
  invalid: ComputedRef<boolean>
  /** id of the error or helper text, for aria-describedby */
  describedBy: ComputedRef<string | undefined>
}

export const FieldContextKey: InjectionKey<FieldContext> = Symbol('FlixorderFormField')
